import type { UserUuid } from "../identity";
export interface MedicationInteraction {
	name: string;
	severity: 'mild' | 'moderate' | 'severe';
	description: string;
}

export interface DosageGuide {
	label: string;
	description?: string | null;
	dosage: string;
	frequency?: string | null;
	administration_route?: string | null;
	instructions?: string | null;
	duration_days?: number | null;
	refills_authorized?: number | null;
	is_default?: boolean;
}

/** The guide flagged is_default, else the first, else undefined. */
export const defaultDosageGuide = (guides?: DosageGuide[] | null): DosageGuide | undefined => {
	if (!guides || guides.length === 0) return undefined;
	return guides.find((g) => g.is_default) ?? guides[0];
};

export interface MonographIndication {
	condition: string;
	evidence_quality: string;
	notes: string;
}

export interface MonographMonitoringRequirement {
	parameter: string;
	frequency: string;
	rationale: string;
}

export interface MonographDosageForm {
	form: string;
	concentration: string;
	volume: string;
	notes: string;
}

export interface MonographPharmacokinetics {
	half_life?: string;
	bioavailability?: { route: string; value: string }[];
	metabolism?: string;
	protein_binding?: string;
	excretion?: string;
	stability?: string;
	reconstitution?: string;
	onset_of_effects?: string;
}

export interface MonographSpecialPopulations {
	pregnancy?: string;
	lactation?: string;
	paediatric?: string;
	renal_impairment?: string;
	hepatic_impairment?: string;
	elderly?: string;
}

export interface MonographCombinationNotes {
	compatible_with?: string[];
	synergistic_effects?: string;
	combination_dosing?: string;
}

export interface MedicationCatalogItem {
	id: number;
	company: string | null;
	name: string;
	generic_name?: string;
	brand_names?: string[];
	description?: string;
	medication_category?: string;
	drug_class?: string;
	schedule?: string;
	controlled_substance: boolean;
	high_risk: boolean;
	requires_script: boolean;
	dosage_guides?: DosageGuide[];
	ndc_code?: string;
	pbs_code?: string;
	mims_id?: string;
	escript_template_id?: string;
	contraindications?: string[];
	side_effects?: string[];
	interactions?: MedicationInteraction[];
	metadata?: Record<string, any>;
	is_active: boolean;
	created_by_user_id?: UserUuid;
	admin_medication_id?: number;
	research_notes?: string;
	mechanism_of_action?: string;
	patient_profile_flags?: Array<{
		category: string;
		name: string;
		relevance: string;
		risk_level: 'high' | 'moderate' | 'low';
	}>;
	indications?: MonographIndication[];
	evidence_level?: string;
	warnings?: string[];
	monitoring_requirements?: MonographMonitoringRequirement[];
	pharmacokinetics?: MonographPharmacokinetics;
	special_populations?: MonographSpecialPopulations;
	regulatory_pathway?: string;
	dosage_forms?: MonographDosageForm[];
	combination_notes?: MonographCombinationNotes;
	created_at: string;
	updated_at: string;
}

export const EVIDENCE_LEVEL_LABELS: Record<string, string> = {
	preclinical_only: 'Preclinical Only',
	early_human: 'Early Human',
	limited_controlled_human: 'Limited Controlled Human',
	established_human_trial: 'Established Human Trial',
};

export const defaultCatalogItem: Omit<MedicationCatalogItem, 'id' | 'created_at' | 'updated_at'> = {
	company: null,
	name: '',
	generic_name: '',
	brand_names: [],
	description: '',
	medication_category: undefined,
	drug_class: '',
	schedule: undefined,
	controlled_substance: false,
	high_risk: false,
	requires_script: true,
	dosage_guides: [],
	ndc_code: '',
	pbs_code: '',
	mims_id: '',
	escript_template_id: '',
	contraindications: [],
	side_effects: [],
	interactions: [],
	metadata: {},
	is_active: true,
	admin_medication_id: undefined,
	research_notes: '',
	mechanism_of_action: '',
	patient_profile_flags: [],
};

export const MEDICATION_CATEGORIES = [
	'Analgesics', 'Antibiotics', 'Anticoagulants', 'Antidepressants',
	'Antidiabetics', 'Antihypertensives', 'Corticosteroids', 'Gastro-intestinal',
	'Hormones', 'Immunosuppressants', 'Lipid-lowering', 'Neurological', 'Peptide',
	'Psychiatric', 'Respiratory', 'Supplements', 'Other', 
].map(v => ({ label: v, value: v.toLowerCase() }));

export const FREQUENCY_OPTIONS = [
	'Once daily', 'Twice daily', 'Three times daily', 'Four times daily',
	'Every other day', '5/7 days', 'Weekly', 'Monthly', 'Every morning', 'Every evening',
	'With meals', 'Before meals', 'After meals', 'As needed',
].map(v => ({ label: v, value: v.toLowerCase() }));

export const ADMINISTRATION_ROUTES = [
	'Inhalation',
	'Intramuscular',
	'Intravenous',
	'Nasal',
	'Ophthalmic',
	'Oral',
	'Other',
	'Otic',
	'Rectal',
	'Subcutaneous',
	'Sublingual',
	'Topical',
	'Transdermal',
].map(v => ({ label: v, value: v.toLowerCase() }));

export const AU_SCHEDULES = [
	{ label: 'S2 — Pharmacy Medicine', value: 'S2' },
	{ label: 'S3 — Pharmacist Only', value: 'S3' },
	{ label: 'S4 — Prescription Only', value: 'S4' },
	{ label: 'S8 — Controlled Drug', value: 'S8' },
	{ label: 'Unscheduled', value: 'unscheduled' },
];

export const INTERACTION_SEVERITIES = [
	{ label: 'Mild', value: 'mild' },
	{ label: 'Moderate', value: 'moderate' },
	{ label: 'Severe', value: 'severe' },
];